import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { 
  FiMapPin, 
  FiPhone, 
  FiMail, 
  FiSend, 
  FiCheckCircle, 
  FiPrinter, 
  FiSmartphone 
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import contactData from '../data/contactData';
import ScrollReveal from '../components/ScrollReveal';

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    office: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) {
      setErrors((errs) => ({ ...errs, [name]: '' }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    
    if (!form.email.trim()) {
      newErrors.email = 'Email address is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const digitsOnly = form.phone.replace(/\D/g, '');
      if (digitsOnly.length !== 10) {
        newErrors.phone = 'Phone number must be exactly 10 digits';
      }
    }
    
    if (!form.office) {
      newErrors.office = 'Please select an office';
    }
    
    if (!form.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!form.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      {
        title: 'Contact Form Submission',
        name: form.name,
        email: form.email,
        message: `Phone: ${form.phone}\n` +
                 (form.office ? `Office: ${form.office}\n` : '') +
                 (form.subject ? `Subject: ${form.subject}\n` : '') +
                 `\nMessage:\n${form.message}`
      },
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    )
    .then((response) => {
      setLoading(false);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 30000);
      setForm({ name: '', email: '', phone: '', subject: '', message: '', office: '' });
    })
    .catch((error) => {
      console.error('EmailJS Error:', error);
      setLoading(false);
      alert('Failed to send message. Please try again.');
    });
  };
  
  return (
    <div className="page-wrapper">
      {/* SECTION 1 — Page Banner */}
      <div className="page-hero">
        <ScrollReveal animation="fade-up">
          <h1>{contactData.pageHeading}</h1>
          <div className="service-detail-breadcrumbs" style={{ justifyContent: 'center', marginTop: 12 }}>
            <Link to="/">Home</Link>
            <span className="service-detail-separator">/</span>
            <span className="service-detail-current">Contact Us</span>
          </div>
        </ScrollReveal>
      </div>

      <main className="contact-page-main">
        <div className="container">
          
          {/* SECTION 2 — All 3 Office Cards */}
          <ScrollReveal animation="fade-up" delay={100}>
            <div className="contact-offices-grid">
              {contactData.offices.map((office) => {
                return (
                  <div key={office.id} className="contact-office-card">
                    <h3 className="contact-office-card-heading">{office.label}</h3>
                    <div className="gold-divider" style={{ margin: '8px 0 16px', width: '36px' }} />
                    <div className="contact-office-card-details">
                      <div className="contact-office-detail-item">
                        <FiMapPin size={18} />
                        <span className="contact-office-detail-text">{office.address}</span>
                      </div>
                      
                      {office.phone && office.phone.length > 0 && (
                        <div className="contact-office-detail-item">
                          <FiPhone size={18} />
                          <span className="contact-office-detail-text">
                            Landline: {office.phone.map((p, idx) => (
                              <React.Fragment key={idx}>
                                <a href={`tel:${p.replace(/\s|-/g, '')}`} style={{ textDecoration: 'none', color: 'inherit' }}>{p}</a>
                                {idx < office.phone.length - 1 ? ', ' : ''}
                              </React.Fragment>
                            ))}
                          </span>
                        </div>
                      )}
                      
                      {office.mobile && office.mobile.length > 0 && (
                        <div className="contact-office-detail-item">
                          <FiSmartphone size={18} />
                          <span className="contact-office-detail-text">
                            Mobile: {office.mobile.map((p, idx) => (
                              <React.Fragment key={idx}>
                                <a href={`tel:${p.replace(/\s|-/g, '')}`} style={{ textDecoration: 'none', color: 'inherit' }}>{p}</a>
                                {idx < office.mobile.length - 1 ? ', ' : ''}
                              </React.Fragment>
                            ))}
                          </span>
                        </div>
                      )}
                      
                      {office.email && office.email.length > 0 && (
                        <div className="contact-office-detail-item">
                          <FiMail size={18} />
                          <span className="contact-office-detail-text">
                            {office.email.map((email, idx) => (
                              <span key={idx} style={{ display: 'block' }}>
                                <a href={`mailto:${email}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                  {email}
                                </a>
                              </span>
                            ))}
                          </span>
                        </div>
                      )}
                      
                      {office.fax && (
                        <div className="contact-office-detail-item">
                          <FiPrinter size={18} />
                          <span className="contact-office-detail-text">
                            Fax: {office.fax}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollReveal>
        </div>

        {/* SECTION 3 — Map Section */}
        <ScrollReveal animation="fade-up" delay={200}>
          <section className="contact-maps-section">
            <div className="container">
              <div className="section-header" style={{ marginBottom: 40, textAlign: 'center' }}>
                <span className="section-badge">Locations</span>
                <h2 className="section-title">Find Us on Map</h2>
                <div className="gold-divider" style={{ margin: '16px auto', width: '60px' }} />
              </div>
              <div className="contact-maps-grid">
                {contactData.offices.map((office) => (
                  <div key={office.id} className="contact-map-container">
                    <h4 className="contact-map-label" style={{ textAlign: 'center', marginBottom: 8 }}>
                      {office.label}
                    </h4>
                    <div className="contact-map-iframe-wrap">
                      <iframe 
                        title={`${office.label} Map`}
                        src={office.mapEmbedUrl}
                        allowFullScreen="" 
                        loading="lazy" 
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="container">
          <div className="contact-page-grid" style={{ gridTemplateColumns: '1fr', maxWidth: '800px', margin: '0 auto 48px' }}>
            
            {/* SECTION 4 — Contact Form */}
            <ScrollReveal animation="fade-up" delay={300}>
              <div className="contact-page-form-card">
                <h3 className="contact-page-form-title" style={{ textAlign: 'center' }}>Send Us a Message</h3>
                <p className="contact-page-form-sub" style={{ textAlign: 'center' }}>
                  Fill in the form below and we will get back to you soon.
                </p>

                {submitted ? (
                  <div className="contact-page-success">
                    <FiCheckCircle size={48} className="contact-page-success-icon" />
                    <h4>Message Sent Successfully!</h4>
                    <p>
                      Your message has been sent successfully. We will get back to you soon.
                    </p>
                    <button 
                      className="btn-primary" 
                      onClick={() => { 
                        setSubmitted(false); 
                        setForm({ name: '', email: '', phone: '', office: '', subject: '', message: '' }); 
                      }} 
                      style={{ marginTop: 20 }}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-page-form">
                    <div className="contact-page-row">
                      <div className="contact-page-field">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          style={errors.name ? { borderColor: '#dc3545' } : {}}
                        />
                        {errors.name && <span className="field-error-message">{errors.name}</span>}
                      </div>
                      
                      <div className="contact-page-field">
                        <label>Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          style={errors.email ? { borderColor: '#dc3545' } : {}}
                        />
                        {errors.email && <span className="field-error-message">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="contact-page-row">
                      <div className="contact-page-field">
                        <label>Phone Number *</label>
                        <input
                          type="text"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="10-digit mobile number"
                          style={errors.phone ? { borderColor: '#dc3545' } : {}}
                        />
                        {errors.phone && <span className="field-error-message">{errors.phone}</span>}
                      </div>

                      <div className="contact-page-field">
                        <label>Select Office *</label>
                        <select
                          name="office"
                          value={form.office}
                          onChange={handleChange}
                          className="contact-page-field-select"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: errors.office ? '1.5px solid #dc3545' : '1.5px solid var(--gray-200)',
                            borderRadius: 'var(--radius-sm)',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.9rem',
                            color: 'var(--text-dark)',
                            background: 'var(--off-white)',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="" disabled hidden style={{ display: 'none' }}>Select Office</option>
                          {contactData.offices.map((office) => (
                            <option key={office.id} value={office.label}>{office.label}</option>
                          ))}
                        </select>
                        {errors.office && <span className="field-error-message">{errors.office}</span>}
                      </div>
                    </div>

                    <div className="contact-page-field">
                      <label>Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        placeholder="Subject of your enquiry"
                        style={errors.subject ? { borderColor: '#dc3545' } : {}}
                      />
                      {errors.subject && <span className="field-error-message">{errors.subject}</span>}
                    </div>

                    <div className="contact-page-field">
                      <label>Message *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Tell us about your requirements..."
                        rows={5}
                        style={errors.message ? { borderColor: '#dc3545' } : {}}
                      />
                      {errors.message && <span className="field-error-message">{errors.message}</span>}
                    </div>

                    <button type="submit" className="btn-primary" disabled={loading} style={{ alignSelf: 'center', minWidth: '160px', justifyContent: 'center' }}>
                      {loading ? (
                        <span className="contact-page-spinner" />
                      ) : (
                        <><FiSend size={15} /> Send Message</>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>


        </div>
      </main>
    </div>
  );
}
